import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, TrendingUp, MapPin, DollarSign, Users, ChevronDown, ChevronUp, Download, Mail, Scale, IndianRupee, Lock, Unlock, CreditCard } from 'lucide-react';
import { states, collegeTypes, branches } from '../mockData';
import { toast } from '../hooks/use-toast';
import jsPDF from 'jspdf';
import axios from 'axios';
import Footer from '../components/Footer';
import FeeDetailsPopup from '../components/FeeDetailsPopup';
import CollegeComparison, { ComparisonBar } from '../components/CollegeComparison';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formData = location.state;
  
  // ALL HOOKS MUST BE DECLARED AT THE TOP - before any conditional returns
  // Fetch real colleges from backend instead of using mock data
  const [colleges, setColleges] = useState([]);
  const [loadingColleges, setLoadingColleges] = useState(true);
  
  // Fee popup state
  const [selectedCollegeForFees, setSelectedCollegeForFees] = useState(null);
  const [showFeePopup, setShowFeePopup] = useState(false);
  
  // Comparison state
  const [compareList, setCompareList] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  
  const [filters, setFilters] = useState({
    state: 'All States',
    collegeType: 'All Types',
    branch: 'All Branches',
    maxFees: 500000
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('chance');
  const [expandedCards, setExpandedCards] = useState({});
  
  // Paywall state
  const [paywallSettings, setPaywallSettings] = useState({
    enabled: false,
    free_colleges_count: 5,
    price: 50,
    show_blurred_preview: true
  });
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Payment gateway settings (admin controlled)
  const [paymentGatewaySettings, setPaymentGatewaySettings] = useState({
    cod_enabled: true,
    instamojo_enabled: false
  });
  
  // HS/OS Quota stats
  const [quotaStats, setQuotaStats] = useState({
    home_state: 0,
    other_state: 0,
    all_india: 0,
    user_home_state: ""
  });
  
  // Fetch paywall settings
  useEffect(() => {
    const fetchPaywallSettings = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/paywall/settings`);
        const data = await res.json();
        if (data.success) {
          setPaywallSettings(data.settings);
        }
      } catch (error) {
        console.log('Error fetching paywall settings:', error);
      }
    };
    
    const checkUnlockStatus = async () => {
      const mobile = formData?.mobile || localStorage.getItem('userMobile');
      const email = formData?.email || localStorage.getItem('userEmail');
      const token = localStorage.getItem('paywallUnlockToken');
      
      if (mobile || email || token) {
        try {
          const params = new URLSearchParams();
          if (mobile) params.append('mobile', mobile);
          if (email) params.append('email', email);
          if (token) params.append('token', token);
          
          const res = await fetch(`${BACKEND_URL}/api/paywall/check-unlock?${params}`);
          const data = await res.json();
          if (data.success && data.unlocked) {
            setIsUnlocked(true);
          }
        } catch (error) {
          console.log('Error checking unlock status:', error);
        }
      }
    };
    
    // Fetch payment gateway settings (COD, Instamojo enabled/disabled)
    const fetchPaymentGatewaySettings = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payment/settings`);
        const data = await res.json();
        if (data) {
          setPaymentGatewaySettings({
            cod_enabled: data.cod_enabled ?? true,
            instamojo_enabled: data.instamojo_enabled ?? false
          });
        }
      } catch (error) {
        console.log('Error fetching payment gateway settings:', error);
      }
    };
    
    // Check for payment_id in URL (user returning from Instamojo)
    const verifyPaymentFromURL = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('payment_id');
      
      if (paymentId) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/instamojo/verify/${paymentId}`);
          const data = await res.json();
          
          if (data.success && data.status === 'completed' && data.unlocked) {
            localStorage.setItem('paywallUnlockToken', data.unlock_token);
            setIsUnlocked(true);
            toast({
              title: "Payment Successful!",
              description: "Full results unlocked. You can now see all colleges.",
            });
            
            // Clean up URL
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          } else if (data.success && data.status === 'pending') {
            toast({
              title: "Payment Pending",
              description: "Your payment is being processed. Please wait...",
              variant: "warning"
            });
          }
        } catch (error) {
          console.log('Error verifying payment:', error);
        }
      }
    };
    
    fetchPaywallSettings();
    checkUnlockStatus();
    fetchPaymentGatewaySettings();
    verifyPaymentFromURL();
  }, [formData]);
  
  // Handle paywall unlock
  const handleUnlockPayment = async (paymentMethod = 'cod') => {
    setProcessingPayment(true);
    try {
      if (paymentMethod === 'instamojo') {
        // For Instamojo, create a payment request and redirect
        const res = await fetch(`${BACKEND_URL}/api/paywall/create-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mobile: formData?.mobile || '',
            email: formData?.email || '',
            user_id: localStorage.getItem('userId') || '',
            amount: paywallSettings.price,
            purpose: 'College Prediction Results Unlock',
            buyer_name: formData?.name || 'Student',
            redirect_url: window.location.href
          })
        });
        
        const data = await res.json();
        if (data.success && data.payment_url) {
          // Redirect to Instamojo payment page
          window.location.href = data.payment_url;
          return;
        } else if (data.success && data.mock_payment) {
          // Mock payment for testing - auto unlock
          localStorage.setItem('paywallUnlockToken', data.unlock_token);
          setIsUnlocked(true);
          setShowPaywallModal(false);
          toast({
            title: "Payment Successful!",
            description: "Full results unlocked. You can now see all colleges.",
          });
        } else {
          throw new Error(data.message || 'Failed to create payment request');
        }
      } else {
        // COD - existing logic
        const res = await fetch(`${BACKEND_URL}/api/paywall/unlock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mobile: formData?.mobile,
            email: formData?.email,
            user_id: localStorage.getItem('userId'),
            payment_method: paymentMethod
          })
        });
        
        const data = await res.json();
        if (data.success) {
          localStorage.setItem('paywallUnlockToken', data.unlock_token);
          setIsUnlocked(true);
          setShowPaywallModal(false);
          toast({
            title: "Order Placed!",
            description: "Your COD order is confirmed. Results unlocked.",
          });
        } else {
          throw new Error(data.message || 'Payment failed');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Fetch colleges effect - use prediction API for filtered results
  const [predictedColleges, setPredictedColleges] = useState([]);
  
  useEffect(() => {
    const fetchPredictions = async () => {
      // Don't fetch if no form data
      if (!formData) {
        setLoadingColleges(false);
        return;
      }
      
      // Don't fetch predictions if payment is not confirmed
      // This prevents showing predictions before payment
      if (!formData.paymentConfirmed) {
        setLoadingColleges(false);
        return;
      }
      
      try {
        // Use the new predict API for filtered results with HS/OS support
        const response = await axios.post(`${BACKEND_URL}/api/predict`, {
          rank: parseInt(formData.rank),
          category: formData.category || "General",
          counselling: formData.counselling || "JoSAA",
          exam_type: formData.examType || "JEE Main",
          counselling_round: formData.counsellingRound || 1,
          home_state: formData.homeState || ""  // Pass home state for HS/OS calculation
        });
        
        if (response.data.success) {
          setPredictedColleges(response.data.predictions);
          // Store quota stats for display
          if (response.data.quota_stats) {
            setQuotaStats(response.data.quota_stats);
          }
        }
        setLoadingColleges(false);
      } catch (error) {
        console.error('Error fetching predictions:', error);
        // Fallback to old method if predict API fails
        try {
          const response = await axios.get(`${BACKEND_URL}/api/colleges`);
          setColleges(response.data);
        } catch (e) {
          toast({
            title: "Error",
            description: "Failed to load predictions. Please try again.",
            variant: "destructive"
          });
        }
        setLoadingColleges(false);
      }
    };
    fetchPredictions();
  }, [formData]);

  // Comparison functions
  const toggleCompare = (college) => {
    setCompareList(prev => {
      const exists = prev.find(c => c.id === college.id);
      if (exists) {
        return prev.filter(c => c.id !== college.id);
      }
      if (prev.length >= 3) {
        toast({
          title: "Limit Reached",
          description: "You can compare up to 3 colleges at a time",
          variant: "destructive"
        });
        return prev;
      }
      return [...prev, college];
    });
  };

  const isInCompareList = (collegeId) => {
    return compareList.some(c => c.id === collegeId);
  };

  const clearCompareList = () => {
    setCompareList([]);
  };

  // Fee popup functions
  const openFeeDetails = (college) => {
    setSelectedCollegeForFees(college);
    setShowFeePopup(true);
  };

  // Calculate admission chances
  const calculateChance = (cutoff, userRank) => {
    const difference = userRank - cutoff;
    if (difference <= 0) return 'High';
    if (difference <= cutoff * 0.1) return 'Moderate';
    if (difference <= cutoff * 0.2) return 'Low';
    return 'Very Low';
  };

  const getChanceColor = (chance) => {
    switch(chance) {
      case 'High': return 'bg-green-100 text-green-800 border-green-200';
      case 'Moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Very Low': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryKey = (category) => {
    if (category === 'General') return 'general';
    if (category === 'OBC-NCL' || category === 'OBC') return 'obc';
    if (category === 'SC') return 'sc';
    if (category === 'ST') return 'st';
    if (category === 'EWS') return 'ews';
    return 'general';
  };

  // Generate predictions - use predictedColleges from API if available
  const predictions = useMemo(() => {
    if (!formData) return [];

    // If we have predictions from the API, use those directly
    if (predictedColleges && predictedColleges.length > 0) {
      return predictedColleges.map(p => ({
        collegeName: p.college_name,
        collegeType: p.college_type,
        counsellingTypes: p.counselling_types,
        state: p.state,
        city: p.city,
        branchName: p.branch_name,
        branchCode: p.branch_code || p.branch_name?.substring(0, 4).toUpperCase(),
        cutoff: p.cutoff_rank,
        adjustedCutoff: p.adjusted_cutoff,
        openingRank: p.cutoff_rank,
        closingRank: p.cutoff_rank,
        chance: p.chance,
        fees: p.fees,
        seats: 60,
        placement: p.placement,
        // HS/OS quota fields
        quotaType: p.quota_type,  // "HS", "OS", or "AI"
        hasHsOsQuota: p.has_hs_os_quota,
        hsCutoff: p.hs_cutoff,
        osCutoff: p.os_cutoff,
        collegeHomeState: p.college_home_state,
        isHomeState: p.is_home_state,
        // Historical cutoff data for charts
        historicalCutoffs: p.historical_cutoffs,
        categoryKey: p.category_key
      }));
    }

    // Fallback to client-side filtering if API didn't return results
    const examKey = formData.examType === 'JEE Main' ? 'jeeMain' : 'jeeAdvanced';
    const categoryKey = getCategoryKey(formData.category);
    const userRank = parseInt(formData.rank);
    const selectedCounselling = formData.counselling || 'JoSAA';

    const results = [];

    colleges.forEach(college => {
      // CRITICAL FIX: Filter by counselling type
      if (college.counselling_types && selectedCounselling) {
        const matchesCounselling = college.counselling_types.some(type => {
          // Exact match
          if (type === selectedCounselling) return true;
          
          // Handle "State Counselling" - matches any college with State Counselling type
          if (selectedCounselling === 'State Counselling' && type === 'State Counselling') {
            return true;
          }
          
          // Handle state-specific counselling patterns
          if (selectedCounselling.includes('Admission') || selectedCounselling.includes('UPTAC') || 
              selectedCounselling.includes('JAC') || selectedCounselling.includes('CAP') ||
              selectedCounselling.includes('IPU') || selectedCounselling.includes('CET') ||
              selectedCounselling.includes('IIIT') || selectedCounselling.includes('BCECE') ||
              selectedCounselling.includes('OJEE') || selectedCounselling.includes('JCECE') ||
              selectedCounselling.includes('EAMCET') || selectedCounselling.includes('PET') ||
              selectedCounselling.includes('CEE') || selectedCounselling.includes('JEE') ||
              selectedCounselling.includes('CENTAC') || selectedCounselling.includes('JKCET')) {
            const firstWord = selectedCounselling.split(' ')[0];
            return type.includes(firstWord) || college.state?.includes(firstWord) || 
                   college.name.toLowerCase().includes(firstWord.toLowerCase());
          }
          
          return false;
        });
        
        if (!matchesCounselling) {
          return;
        }
      }

      college.branches.forEach(branch => {
        const cutoffData = branch.cutoffs?.[examKey];
        if (!cutoffData) return;

        const latestYear = Math.max(...Object.keys(cutoffData).map(Number));
        const cutoffObj = cutoffData[latestYear]?.[categoryKey];
        
        if (!cutoffObj) return;

        const cutoffRank = cutoffObj.closing || cutoffObj;
        const chance = calculateChance(cutoffRank, userRank);

        results.push({
          collegeName: college.name,
          collegeType: college.type,
          counsellingTypes: college.counselling_types,
          state: college.state,
          city: college.city,
          branchName: branch.name,
          branchCode: branch.code || branch.name.substring(0, 4).toUpperCase(),
          cutoff: cutoffRank,
          openingRank: cutoffObj.opening,
          closingRank: cutoffObj.closing,
          chance: chance,
          fees: college.fees || branch.fees,
          seats: branch.seats || 60,
          historicalCutoffs: cutoffData,
          categoryKey: categoryKey
        });
      });
    });

    return results;
  }, [formData, colleges, predictedColleges]); // Add predictedColleges to dependency array

  // Apply filters and sorting - MUST be before any conditional returns
  const filteredResults = useMemo(() => {
    let filtered = predictions.filter(pred => {
      // Search filter - searches in college name, branch, city, state
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const collegeName = (pred.name || pred.collegeName || '').toLowerCase();
        const branch = (pred.branch || pred.branchName || '').toLowerCase();
        const city = (pred.city || pred.location || '').toLowerCase();
        const state = (pred.state || '').toLowerCase();
        const cutoff = (pred.cutoff || '').toString();
        
        const matchesSearch = collegeName.includes(query) || 
                             branch.includes(query) || 
                             city.includes(query) || 
                             state.includes(query) ||
                             cutoff.includes(query);
        
        if (!matchesSearch) return false;
      }
      
      // Existing filters
      if (filters.state !== 'All States' && pred.state !== filters.state) return false;
      if (filters.collegeType !== 'All Types' && pred.collegeType !== filters.collegeType) return false;
      if (filters.branch !== 'All Branches' && pred.branchName !== filters.branch) return false;
      if (pred.fees > filters.maxFees) return false;
      return true;
    });

    // Sort results
    if (sortBy === 'chance') {
      const chanceOrder = { 'High': 0, 'Moderate': 1, 'Low': 2, 'Very Low': 3 };
      filtered.sort((a, b) => chanceOrder[a.chance] - chanceOrder[b.chance]);
    } else if (sortBy === 'cutoff') {
      filtered.sort((a, b) => a.cutoff - b.cutoff);
    } else if (sortBy === 'fees') {
      filtered.sort((a, b) => a.fees - b.fees);
    }

    return filtered;
  }, [predictions, filters, sortBy, searchQuery]);

  const toggleExpand = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const savePredictionToBackend = useCallback(async (userId, userName) => {
    try {
      const predictionData = {
        user_id: userId,
        rank: parseInt(formData?.rank || 0),
        exam_type: formData?.examType || 'JEE Main',
        category: formData?.category || 'General',
        state: formData?.homeState || null,
        counselling: formData?.counselling || 'JoSAA',
        counselling_round: formData?.counsellingRound || 1,
        matched_colleges: predictions.length,
        predictions: predictions.slice(0, 50).map(p => ({
          college: p.collegeName,
          branch: p.branchName,
          chance: p.chance,
          cutoff: p.cutoff || p.adjustedCutoff,
          quota_type: p.quotaType
        }))
      };

      await axios.post(`${BACKEND_URL}/api/predictions/save`, predictionData);
      
      // Mark as saved to prevent duplicate saves
      if (formData) {
        formData.savedPrediction = true;
      }
      
      toast({ 
        title: "Prediction Saved", 
        description: `Saved to ${userName}'s account. View in "My Predictions"`,
        duration: 5000
      });
    } catch (error) {
      console.error('Failed to save prediction:', error);
      // Don't show error to user as this is a background operation
    }
  }, [formData, predictions]);

  // Save prediction if user is logged in
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');
    
    if (userId && formData && !formData.savedPrediction && predictions.length > 0) {
      savePredictionToBackend(userId, userName);
    }
  }, [predictions, formData, savePredictionToBackend]);

  const handleDownloadPDF = async () => {
    try {
      if (filteredResults.length === 0) {
        toast({ 
          title: "No Data", 
          description: "No colleges found to export", 
          variant: "destructive" 
        });
        return;
      }
      
      // Fetch PDF template settings
      const templateResponse = await axios.get(`${BACKEND_URL}/api/pdf-template`);
      const template = templateResponse.data;
      
      // Use form data name first, then localStorage, then default
      const userName = formData?.name || localStorage.getItem('userName') || 'Student';
      const doc = new jsPDF('landscape'); // Use landscape for more width
      
      // Helper function to convert hex to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
      };
      
      // Color mapping for chance levels
      const chanceColors = {
        'High': { r: 34, g: 197, b: 94 },      // Green
        'Moderate': { r: 234, g: 179, b: 8 },   // Yellow/Amber
        'Low': { r: 249, g: 115, b: 22 },       // Orange
        'Very Low': { r: 239, g: 68, b: 68 }    // Red
      };
      
      const headerColorRgb = hexToRgb(template.headerColor || '#2563eb');
      const tableHeaderBgRgb = hexToRgb(template.tableHeaderBgColor || '#2563eb');
      const tableHeaderTextRgb = hexToRgb(template.tableHeaderTextColor || '#ffffff');
      
      // Header
      doc.setFontSize(template.fontSize?.header || 20);
      doc.setTextColor(headerColorRgb.r, headerColorRgb.g, headerColorRgb.b);
      doc.text(template.header || 'JEE Predictor', 148, 15, { align: 'center' });
      
      doc.setFontSize(template.fontSize?.subheader || 12);
      doc.setTextColor(100);
      doc.text(template.subheader || 'College Prediction Report', 148, 22, { align: 'center' });
      
      let y = 35;
      
      // User info section with Counselling type
      if (template.includeUserInfo !== false) {
        // Draw info box
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(15, y - 5, 267, 28, 3, 3, 'F');
        
        doc.setFontSize(template.fontSize?.body || 10);
        doc.setTextColor(0, 0, 0);
        
        // Left column
        doc.setFont(undefined, 'bold');
        doc.text('Name:', 20, y + 2);
        doc.text('Exam:', 20, y + 9);
        doc.text('Rank:', 20, y + 16);
        
        doc.setFont(undefined, 'normal');
        doc.text(userName, 45, y + 2);
        doc.text(formData?.examType || 'N/A', 45, y + 9);
        doc.text((formData?.rank || 'N/A').toString(), 45, y + 16);
        
        // Middle column
        doc.setFont(undefined, 'bold');
        doc.text('Category:', 100, y + 2);
        doc.text('Counselling:', 100, y + 9);
        doc.text('State:', 100, y + 16);
        
        doc.setFont(undefined, 'normal');
        doc.text(formData?.category || 'N/A', 135, y + 2);
        doc.text(formData?.counselling || 'JoSAA', 135, y + 9);
        doc.text(formData?.homeState || 'N/A', 135, y + 16);
        
        // Right column
        doc.setFont(undefined, 'bold');
        doc.text('Date:', 200, y + 2);
        doc.text('Total Results:', 200, y + 9);
        
        doc.setFont(undefined, 'normal');
        doc.text(new Date().toLocaleDateString(), 240, y + 2);
        doc.text(filteredResults.length.toString(), 240, y + 9);
        
        y += 32;
      }
      
      // Custom Sections
      if (template.customSections && template.customSections.length > 0) {
        template.customSections.forEach((section) => {
          if (section.enabled) {
            doc.setFontSize(template.fontSize?.subheader || 12);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'bold');
            doc.text(section.title, 15, y);
            y += 7;
            
            doc.setFontSize(template.fontSize?.body || 10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(60, 60, 60);
            
            const maxWidth = 260;
            const contentLines = doc.splitTextToSize(section.content, maxWidth);
            
            contentLines.forEach((line) => {
              if (y > 190) {
                doc.addPage();
                y = 20;
              }
              doc.text(line, 15, y);
              y += 5;
            });
            
            y += 5;
          }
        });
      }
      
      // Draw table with better column widths for landscape
      const lineHeight = 7;
      const pageHeight = 190;
      
      // Column positions for landscape (total width ~280)
      const cols = {
        num: 15,
        college: 25,
        branch: 115,
        cutoff: 175,
        chance: 205,
        location: 245
      };
      
      // Table header
      doc.setFillColor(tableHeaderBgRgb.r, tableHeaderBgRgb.g, tableHeaderBgRgb.b);
      doc.rect(12, y, 273, 8, 'F');
      doc.setTextColor(tableHeaderTextRgb.r, tableHeaderTextRgb.g, tableHeaderTextRgb.b);
      doc.setFontSize(template.fontSize?.table || 9);
      doc.setFont(undefined, 'bold');
      doc.text('#', cols.num, y + 5);
      doc.text('College Name', cols.college, y + 5);
      doc.text('Branch', cols.branch, y + 5);
      doc.text('Cutoff', cols.cutoff, y + 5);
      doc.text('Chance', cols.chance, y + 5);
      doc.text('Location', cols.location, y + 5);
      
      y += 10;
      doc.setFont(undefined, 'normal');
      
      // Table rows with colored chance badges
      filteredResults.slice(0, 40).forEach((result, index) => {
        if (y > pageHeight) {
          doc.addPage();
          y = 20;
          
          // Repeat header on new page
          doc.setFillColor(tableHeaderBgRgb.r, tableHeaderBgRgb.g, tableHeaderBgRgb.b);
          doc.rect(12, y, 273, 8, 'F');
          doc.setTextColor(tableHeaderTextRgb.r, tableHeaderTextRgb.g, tableHeaderTextRgb.b);
          doc.setFont(undefined, 'bold');
          doc.text('#', cols.num, y + 5);
          doc.text('College Name', cols.college, y + 5);
          doc.text('Branch', cols.branch, y + 5);
          doc.text('Cutoff', cols.cutoff, y + 5);
          doc.text('Chance', cols.chance, y + 5);
          doc.text('Location', cols.location, y + 5);
          y += 10;
          doc.setFont(undefined, 'normal');
        }
        
        // Alternate row background
        if (index % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(12, y - 4, 273, lineHeight, 'F');
        }
        
        const collegeName = (result.name || result.collegeName || 'Unknown').substring(0, 45);
        const branch = (result.branch || result.branchName || result.branchCode || 'N/A').substring(0, 28);
        const cutoff = (result.cutoff || 'N/A').toString();
        const chance = (result.chance || 'Medium');
        
        // Format location
        let location = '';
        if (result.city && result.state && result.state !== 'Various') {
          location = `${result.city}, ${result.state}`;
        } else if (result.city) {
          location = result.city;
        } else if (result.state && result.state !== 'Various') {
          location = result.state;
        }
        location = location.substring(0, 30);
        
        doc.setFontSize(template.fontSize?.table || 8);
        doc.setTextColor(0, 0, 0);
        doc.text((index + 1).toString(), cols.num, y);
        doc.text(collegeName, cols.college, y);
        doc.text(branch, cols.branch, y);
        doc.text(cutoff, cols.cutoff, y);
        
        // Draw colored badge for chance
        const chanceColor = chanceColors[chance] || { r: 107, g: 114, b: 128 };
        const chanceWidth = doc.getTextWidth(chance) + 6;
        doc.setFillColor(chanceColor.r, chanceColor.g, chanceColor.b);
        doc.roundedRect(cols.chance - 2, y - 3.5, chanceWidth, 5, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.text(chance, cols.chance, y);
        
        doc.setTextColor(0, 0, 0);
        doc.text(location, cols.location, y);
        
        // Draw subtle line
        doc.setDrawColor(230, 230, 230);
        doc.line(12, y + 2.5, 285, y + 2.5);
        
        y += lineHeight;
      });
      
      // Legend for chance colors
      y = pageHeight + 5;
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text('Chance Legend:', 15, y);
      
      const legendItems = [
        { label: 'High', color: chanceColors['High'] },
        { label: 'Moderate', color: chanceColors['Moderate'] },
        { label: 'Low', color: chanceColors['Low'] },
        { label: 'Very Low', color: chanceColors['Very Low'] }
      ];
      
      let legendX = 55;
      legendItems.forEach(item => {
        doc.setFillColor(item.color.r, item.color.g, item.color.b);
        doc.roundedRect(legendX, y - 3, 4, 4, 1, 1, 'F');
        doc.setTextColor(60, 60, 60);
        doc.text(item.label, legendX + 6, y);
        legendX += 35;
      });
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(template.footer || `Generated by JEE Predictor | ${new Date().toLocaleDateString()}`, 148, 205, { align: 'center' });
      
      // Save
      const filename = `JEE_Prediction_${userName.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      doc.save(filename);
      
      toast({ 
        title: "Success!", 
        description: "PDF downloaded successfully",
        duration: 3000
      });
    } catch (error) {
      console.error('PDF error:', error);
      toast({ 
        title: "Error", 
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive" 
      });
    }
  };

  const handleEmailReport = async () => {
    try {
      const userName = localStorage.getItem('userName') || 'Student';
      const userEmail = localStorage.getItem('userEmail') || formData.email;
      
      if (!userEmail) {
        toast({
          title: "Email needed",
          description: "Sign in to receive your detailed prediction report via email",
          variant: "default"
        });
        return;
      }
      
      // Create summary of top predictions
      const topPredictions = filteredResults.slice(0, 5).map((r, i) => {
        const collegeName = r.name || r.collegeName || 'Unknown College';
        const branch = r.branch || r.branchCode || 'N/A';
        const chance = r.chance || 'Medium';
        return `${i+1}. ${collegeName} - ${branch} (${chance} chance)`;
      }).join('\n');
      
      const reportSummary = `
Exam: ${formData.examType}
Rank: ${formData.rank}
Category: ${formData.category}
State: ${formData.homeState || 'N/A'}

Top 5 College Predictions:
${topPredictions}

Total Colleges Found: ${filteredResults.length}

Login to your account to download the detailed PDF report.
      `;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/email/send-report`,
        null,
        {
          params: {
            user_email: userEmail,
            user_name: userName,
            report_summary: reportSummary
          }
        }
      );
      
      if (response.data.success) {
        toast({ 
          title: "Email Sent!", 
          description: `Report sent to ${userEmail}`,
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Email sending error:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to send email. Please configure SMTP settings in admin panel.",
        variant: "destructive" 
      });
    }
  };

  // ALL CONDITIONAL RETURNS MUST BE AFTER ALL HOOKS
  // Loading state
  if (loadingColleges) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-lg text-gray-600">Loading colleges data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect if no form data
  if (!formData) {
    navigate('/');
    return null;
  }

  // Check if payment is confirmed - redirect if not
  if (!formData.paymentConfirmed) {
    navigate('/payment', { state: formData });
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            <div className="text-right">
              <p className="text-sm text-slate-600">Your Rank: <span className="font-bold text-slate-900">{formData.rank}</span></p>
              <p className="text-xs text-slate-500">{formData.examType} | {formData.category}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Prediction Summary Header */}
        <Card className="mb-6 shadow-lg border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">📊 Your Prediction Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Row 1 */}
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600 font-medium">Rank:</span>
                    <span className="font-bold text-blue-700">{formData.rank}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600 font-medium">Category:</span>
                    <Badge variant="outline" className="bg-white">{formData.category}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600 font-medium">Counselling:</span>
                    <Badge className="bg-indigo-600">{formData.counselling || 'JoSAA'}</Badge>
                  </div>
                  {/* Row 2 */}
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600 font-medium">Home State:</span>
                    <span className="font-semibold text-slate-800">{formData.homeState || 'Not Selected'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600 font-medium">Quota Eligible:</span>
                    <Badge variant="outline" className={`${formData.homeState ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100'}`}>
                      {formData.homeState ? 'Home State (HS)' : 'Other State (OS)'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600 font-medium">Round:</span>
                    <Badge className={`${formData.counsellingRound > 1 ? 'bg-orange-500' : 'bg-green-600'}`}>
                      Round {formData.counsellingRound || 1}{formData.counsellingRound === 6 ? ' (Spot)' : ''}
                    </Badge>
                  </div>
                </div>
                {/* Additional Info */}
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">💡 Tip:</span> Predictions are based on historical cutoff data{formData.counsellingRound > 1 ? ` with ${(formData.counsellingRound - 1) * 5}% relaxation for Round ${formData.counsellingRound}` : ''}. 
                    {formData.homeState && (
                      <span> As a <strong>{formData.homeState}</strong> resident, you&apos;re eligible for Home State quota in NITs and IIITs located in your state.</span>
                    )}
                  </p>
                </div>
                
                {/* HS/OS Quota Stats */}
                {(quotaStats.home_state > 0 || quotaStats.other_state > 0) && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-semibold text-slate-700">📊 Quota Distribution:</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {quotaStats.home_state > 0 && (
                        <Badge className="bg-green-100 text-green-700 border border-green-300 px-3 py-1">
                          🏠 Home State (HS): {quotaStats.home_state} colleges
                        </Badge>
                      )}
                      {quotaStats.other_state > 0 && (
                        <Badge className="bg-orange-100 text-orange-700 border border-orange-300 px-3 py-1">
                          🌍 Other State (OS): {quotaStats.other_state} colleges
                        </Badge>
                      )}
                      {quotaStats.all_india > 0 && (
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-300 px-3 py-1">
                          🇮🇳 All India (AI): {quotaStats.all_india} colleges
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      HS quota has ~20% higher cutoffs (easier to get in). IITs have All India quota only.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* JEE Rank Booster CTA */}
        <Card className="mb-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 shadow-xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-full">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-1">🚀 Want to Improve Your JEE Rank?</h3>
                <p className="text-white/90 text-sm">
                  Join our <strong>Ranker Booster Series</strong> with 500+ practice questions, full mock tests, and AI-powered analytics. 
                  Start with <strong>5 FREE tests</strong> today!
                </p>
              </div>
              <Button 
                onClick={() => navigate('/test-series')}
                className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6"
              >
                Start Free Trial <ArrowLeft className="ml-2 w-4 h-4 rotate-180" />
              </Button>
            </div>
            <div className="flex gap-6 mt-4 text-sm text-white/80">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                5 Free Tests
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Detailed Analytics
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                All India Ranking
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Video Solutions
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card className="mb-8 shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl">Filter & Sort Results</CardTitle>
            <CardDescription>Narrow down your college options</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="mb-6">
              <label className="text-sm font-medium text-slate-700 mb-2 block">Search</label>
              <div className="relative">
                <Input
                  placeholder="Search by college name, branch, city, state, or rank cutoff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full"
                />
                <svg className="w-5 h-5 text-slate-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-sm text-blue-600 mt-2">
                  {filteredResults.length} results found for &quot;{searchQuery}&quot;
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">State</label>
                <Select value={filters.state} onValueChange={(value) => setFilters({...filters, state: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">College Type</label>
                <Select value={filters.collegeType} onValueChange={(value) => setFilters({...filters, collegeType: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {collegeTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Branch</label>
                <Select value={filters.branch} onValueChange={(value) => setFilters({...filters, branch: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Sort By</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chance">Admission Chance</SelectItem>
                    <SelectItem value="cutoff">Cutoff Rank</SelectItem>
                    <SelectItem value="fees">Fees</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Header with Actions */}
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Predicted Colleges</h2>
            <p className="text-slate-600">Found {filteredResults.length} matching colleges</p>
          </div>
          <div className="flex gap-3">
            {/* PDF Download - only for unlocked users or if paywall is disabled */}
            {(!paywallSettings.enabled || isUnlocked) ? (
              <>
                <Button onClick={handleDownloadPDF} className="gap-2 bg-green-600 hover:bg-green-700">
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
                <Button onClick={handleEmailReport} variant="outline" className="gap-2">
                  <Mail className="w-4 h-4" />
                  Email Report
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => setShowPaywallModal(true)} 
                  className="gap-2 bg-gray-400 hover:bg-gray-500"
                  title="Unlock to download PDF"
                >
                  <Lock className="w-4 h-4" />
                  Download PDF
                </Button>
                <Button 
                  onClick={() => setShowPaywallModal(true)} 
                  variant="outline" 
                  className="gap-2 text-gray-400"
                  title="Unlock to email report"
                >
                  <Lock className="w-4 h-4" />
                  Email Report
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {/* Paywall Info Banner */}
          {paywallSettings.enabled && !isUnlocked && filteredResults.length > paywallSettings.free_colleges_count && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <Lock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">
                    Showing {paywallSettings.free_colleges_count} of {filteredResults.length} colleges
                  </p>
                  <p className="text-sm text-amber-600">
                    Unlock all colleges for just ₹{paywallSettings.price}
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowPaywallModal(true)} className="bg-amber-500 hover:bg-amber-600">
                <Unlock className="w-4 h-4 mr-2" /> Unlock All
              </Button>
            </div>
          )}
          
          {filteredResults.map((result, index) => {
            // Check if this result should be locked
            const isLocked = paywallSettings.enabled && !isUnlocked && index >= paywallSettings.free_colleges_count;
            
            // Find the full college object for comparison
            const fullCollege = colleges.find(c => c.name === result.collegeName) || {
              id: result.collegeId || `college-${index}`,
              name: result.collegeName,
              type: result.collegeType,
              state: result.state,
              city: result.city,
              fees: { min: result.fees, max: result.fees },
              hostelFees: result.hostelFees,
              messFees: result.messFees,
              nirf_rank: result.nirf_rank,
              naac_grade: result.naac_grade,
              branches: result.branches,
              established: result.established
            };
            
            // Render locked card
            if (isLocked) {
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 border-slate-200 bg-slate-50/50 relative overflow-hidden">
                  <div className="absolute inset-0 backdrop-blur-sm bg-white/60 z-10 flex items-center justify-center">
                    <div className="text-center p-6">
                      <Lock className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                      <p className="font-semibold text-slate-600 mb-2">College #{index + 1} - Locked</p>
                      <p className="text-sm text-slate-500 mb-4">Unlock to see {paywallSettings.show_blurred_preview ? 'full details' : 'this college'}</p>
                      <Button size="sm" onClick={() => setShowPaywallModal(true)} className="bg-blue-600 hover:bg-blue-700">
                        <Unlock className="w-4 h-4 mr-2" /> Unlock for ₹{paywallSettings.price}
                      </Button>
                    </div>
                  </div>
                  {/* Blurred preview */}
                  {paywallSettings.show_blurred_preview && (
                    <CardHeader className="blur-sm select-none">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <CardTitle className="text-xl">████████ College</CardTitle>
                            <Badge variant="outline" className="text-xs">{result.collegeType}</Badge>
                          </div>
                          <CardDescription className="text-base">████████ Engineering</CardDescription>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              ████, ████
                            </span>
                            <span className="flex items-center gap-1">
                              <IndianRupee className="w-4 h-4" />
                              ₹█.█L/year
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={`${getChanceColor(result.chance)} text-sm px-4 py-1`}>
                            {result.chance} Chance
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                  )}
                </Card>
              );
            }
            
            return (
            <Card key={index} className={`hover:shadow-lg transition-all duration-300 border-slate-200 ${isInCompareList(fullCollege.id) ? 'ring-2 ring-blue-500' : ''}`}>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <CardTitle className="text-xl">{result.collegeName}</CardTitle>
                      <Badge variant="outline" className="text-xs">{result.collegeType}</Badge>
                      {/* HS/OS Quota Badge */}
                      {result.hasHsOsQuota && (
                        <Badge 
                          className={`text-xs ${
                            result.quotaType === 'HS' 
                              ? 'bg-green-100 text-green-700 border border-green-300' 
                              : 'bg-orange-100 text-orange-700 border border-orange-300'
                          }`}
                        >
                          {result.quotaType === 'HS' ? '🏠 Home State' : '🌍 Other State'}
                        </Badge>
                      )}
                      {!result.hasHsOsQuota && result.quotaType === 'AI' && (
                        <Badge className="text-xs bg-blue-100 text-blue-700 border border-blue-300">
                          🇮🇳 All India
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="text-base">{result.branchName}</CardDescription>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-600 flex-wrap">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {result.city && result.state && result.state !== 'Various' 
                          ? `${result.city}, ${result.state}`
                          : result.city || result.state || 'India'}
                      </span>
                      <button 
                        onClick={() => openFeeDetails(fullCollege)}
                        className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer"
                      >
                        <IndianRupee className="w-4 h-4" />
                        <span className="underline decoration-dotted">₹{(result.fees / 100000).toFixed(1)}L/year</span>
                      </button>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {result.seats} seats
                      </span>
                      {/* Show college's home state for HS/OS eligible colleges */}
                      {result.hasHsOsQuota && result.collegeHomeState && (
                        <span className="text-xs text-slate-500">
                          (College in {result.collegeHomeState})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={`${getChanceColor(result.chance)} text-sm px-4 py-1`}>
                      {result.chance} Chance
                    </Badge>
                    <p className="text-sm text-slate-600">
                      Cutoff: <span className="font-bold text-slate-900">{result.adjustedCutoff || result.cutoff}</span>
                      {result.hasHsOsQuota && result.hsCutoff && result.osCutoff && (
                        <span className="text-xs ml-1 text-slate-400">
                          (HS: {result.hsCutoff} | OS: {result.osCutoff})
                        </span>
                      )}
                    </p>
                    {/* Compare and Fee buttons */}
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant={isInCompareList(fullCollege.id) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleCompare(fullCollege)}
                        className="text-xs"
                      >
                        <Scale className="w-3 h-3 mr-1" />
                        {isInCompareList(fullCollege.id) ? 'Added' : 'Compare'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openFeeDetails(fullCollege)}
                        className="text-xs"
                      >
                        <IndianRupee className="w-3 h-3 mr-1" />
                        Fees
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpand(index)}
                  className="w-full flex items-center justify-between text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    View Historical Cutoff Trends
                  </span>
                  {expandedCards[index] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                
                {expandedCards[index] && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-3">5-Year Cutoff Trends ({formData.category})</h4>
                    
                    {/* Cutoff Cards */}
                    <div className="grid grid-cols-5 gap-3 mb-4">
                      {Object.entries(result.historicalCutoffs || {})
                        .sort(([yearA], [yearB]) => yearB - yearA)
                        .slice(0, 5)
                        .map(([year, cutoffs]) => {
                          const cutoffData = cutoffs[result.categoryKey];
                          const cutoffValue = typeof cutoffData === 'object' ? cutoffData?.closing : cutoffData;
                          return (
                            <div key={year} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                              <p className="text-xs text-slate-600 mb-1">Year {year}</p>
                              <p className="text-xl font-bold text-blue-600">{cutoffValue || 'N/A'}</p>
                            </div>
                          );
                        })}
                    </div>
                    
                    {/* Trend Visualization */}
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-2">Cutoff Trend Analysis</p>
                      <div className="flex items-end justify-between h-24 gap-2">
                        {Object.entries(result.historicalCutoffs || {})
                          .sort(([yearA], [yearB]) => yearA - yearB)
                          .slice(0, 5)
                          .map(([year, cutoffs], idx, arr) => {
                            const cutoffData = cutoffs[result.categoryKey];
                            const cutoffValue = typeof cutoffData === 'object' ? cutoffData?.closing : cutoffData;
                            
                            // Calculate max/min from array
                            const allValues = arr.map(([, c]) => {
                              const d = c[result.categoryKey];
                              return typeof d === 'object' ? d?.closing : d;
                            }).filter(v => v != null);
                            
                            const maxCutoff = Math.max(...allValues);
                            const minCutoff = Math.min(...allValues);
                            const range = maxCutoff - minCutoff || 1;
                            const heightPercent = cutoffValue ? ((cutoffValue - minCutoff) / range) * 100 : 0;
                            const height = 20 + (heightPercent * 0.6);
                            
                            return (
                              <div key={year} className="flex-1 flex flex-col items-center">
                                <div 
                                  className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all hover:from-blue-700 hover:to-blue-500"
                                  style={{ height: `${height}%` }}
                                  title={`${year}: ${cutoffValue || 'N/A'}`}
                                />
                                <p className="text-xs text-slate-600 mt-1">{year}</p>
                              </div>
                            );
                          })}
                      </div>
                      <div className="mt-3 text-xs text-slate-600 text-center">
                        {(() => {
                          const years = Object.entries(result.historicalCutoffs || {}).sort(([a], [b]) => a - b);
                          if (years.length < 2) return 'Insufficient data for trend analysis';
                          
                          const firstCutoff = years[0][1][result.categoryKey];
                          const lastCutoff = years[years.length - 1][1][result.categoryKey];
                          const firstYear = typeof firstCutoff === 'object' ? firstCutoff?.closing : firstCutoff;
                          const lastYear = typeof lastCutoff === 'object' ? lastCutoff?.closing : lastCutoff;
                          
                          if (!firstYear || !lastYear) return 'Cutoff data not available';
                          
                          const change = lastYear - firstYear;
                          const trend = change > 0 ? 'increasing' : change < 0 ? 'decreasing' : 'stable';
                          return `Cutoff trend: ${trend} (${change > 0 ? '+' : ''}${change} over ${years.length} years)`;
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })}
        </div>

        {filteredResults.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-slate-600 text-lg">No colleges found matching your criteria. Try adjusting the filters.</p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Fee Details Popup */}
      <FeeDetailsPopup 
        college={selectedCollegeForFees}
        isOpen={showFeePopup}
        onClose={() => setShowFeePopup(false)}
      />
      
      {/* College Comparison Modal */}
      <CollegeComparison 
        colleges={compareList}
        isOpen={showComparison}
        onClose={() => setShowComparison(false)}
      />
      
      {/* Comparison Bar */}
      <ComparisonBar 
        selectedColleges={compareList}
        onRemove={(id) => setCompareList(prev => prev.filter(c => c.id !== id))}
        onCompare={() => setShowComparison(true)}
        onClear={clearCompareList}
      />
      
      {/* Paywall Unlock Modal */}
      <Dialog open={showPaywallModal} onOpenChange={setShowPaywallModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-blue-600" />
              Unlock Full College List
            </DialogTitle>
            <DialogDescription>
              Get access to all {filteredResults.length} colleges matching your rank
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">₹{paywallSettings.price}</p>
              <p className="text-sm text-blue-700">One-time payment</p>
              {paywallSettings.discount_text && (
                <Badge className="mt-2 bg-green-500">{paywallSettings.discount_text}</Badge>
              )}
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">What you'll get:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> All {filteredResults.length} matching colleges
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Detailed fee breakdown for each college
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> College comparison feature
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Download PDF report
                </li>
              </ul>
            </div>
            
            <div className="border-t pt-4 space-y-3">
              {/* Payment Options */}
              <h4 className="font-semibold text-sm">Choose Payment Method:</h4>
              
              {/* Online Payment - Instamojo (conditionally rendered) */}
              {paymentGatewaySettings.instamojo_enabled && (
                <Button 
                  onClick={() => handleUnlockPayment('instamojo')} 
                  disabled={processingPayment}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="pay-instamojo-btn"
                >
                  {processingPayment ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⏳</span> Processing...
                    </span>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" /> Pay ₹{paywallSettings.price} Online (UPI/Card/Net Banking)
                    </>
                  )}
                </Button>
              )}
              
              {/* COD Option (conditionally rendered) */}
              {paymentGatewaySettings.cod_enabled && (
                <Button 
                  onClick={() => handleUnlockPayment('cod')} 
                  disabled={processingPayment}
                  variant="outline"
                  className="w-full border-green-500 text-green-700 hover:bg-green-50"
                  data-testid="pay-cod-btn"
                >
                  <IndianRupee className="w-4 h-4 mr-2" /> Pay ₹{paywallSettings.price} - Cash on Delivery
                </Button>
              )}
              
              {/* Show message if no payment methods are enabled */}
              {!paymentGatewaySettings.instamojo_enabled && !paymentGatewaySettings.cod_enabled && (
                <div className="text-center py-4 text-slate-500">
                  <p className="text-sm">No payment methods currently available.</p>
                  <p className="text-xs mt-1">Please contact support for assistance.</p>
                </div>
              )}
              
              <p className="text-xs text-center text-slate-500">
                🔒 Secure payment • Instant access after confirmation
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default Results;